import type { PreviewFitMode } from "./previewFitMode";

import { getScrollbarThickness } from "./scrollbarThickness";
import { visibleHeight } from "./visibleHeight";
import { clampZoom } from "./zoom";

export type PageSize = { width: number; height: number };

/** Gap the page column keeps from the viewport edge when fitting. */
const FIT_PADDING = 32;

/** The vertical margin each page keeps, which does not scale with zoom. */
const PAGE_MARGIN = 8;

/** The viewport as a fit sees it, in pixels. */
export interface FitBox {
  /** Its width without a vertical scrollbar, whether or not one is showing now. */
  width: number;
  height: number;
  /** How much of its height a reader can see at once. */
  visibleHeight: number;
  /** How wide a vertical scrollbar is, or 0 where scrollbars overlay the content. */
  scrollbar: number;
}

/**
 * The zoom that fits `page` to the box. "auto" is actual size, shrunk only when
 * the page is wider than the box.
 */
export function fitZoom(
  mode: PreviewFitMode | "auto",
  page: PageSize,
  pageSizes: readonly PageSize[],
  box: FitBox
): number {
  const height = (box.visibleHeight - FIT_PADDING) / page.height;
  const scaleFor = (availableWidth: number) => {
    const width = (availableWidth - FIT_PADDING) / page.width;
    if (mode === "page-width") return width;
    if (mode === "page-fit") return Math.min(width, height);
    return Math.min(1, width);
  };
  const unbarred = scaleFor(box.width);
  if (columnHeight(pageSizes, unbarred) <= box.height) return clampZoom(unbarred);
  return clampZoom(scaleFor(box.width - box.scrollbar));
}

/** How tall the page column is at `zoom`. */
function columnHeight(pageSizes: readonly PageSize[], zoom: number): number {
  const pages = pageSizes.reduce((total, size) => total + size.height * zoom, 0);
  return pages + PAGE_MARGIN * (pageSizes.length + 1);
}

/**
 * The viewport's measurements for a fit. Its own width counts a scrollbar it is
 * already showing back in, since the fit decides afresh whether there will be one.
 */
export function measureFitBox(viewport: HTMLElement): FitBox {
  const shownScrollbar = Math.max(0, viewport.offsetWidth - viewport.clientWidth);
  return {
    width: viewport.clientWidth + shownScrollbar,
    height: viewport.clientHeight,
    visibleHeight: visibleHeight(viewport),
    scrollbar: getScrollbarThickness(),
  };
}
